import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useWhatsAppOrder } from "@/hooks/useWhatsAppOrder";

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { openModal } = useWhatsAppOrder();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child) => {
              child.classList.add("animate-in");
            });
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden h-[500px] md:h-[650px] lg:h-[750px]"
    >
      <img
        src="/images/home-hero.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45" />

      <div className="relative z-10 flex flex-col items-center text-center px-4 py-20 md:py-32 lg:py-40 h-full justify-center">
        <span
          data-animate
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-secondary-100/90 text-secondary-800 text-xs font-semibold tracking-widest uppercase mb-8 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600"
        >
          <i className="ri-star-fill w-3 h-3 flex items-center justify-center" />
          New Arrival
        </span>

        <h1
          data-animate
          className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] max-w-3xl mb-6 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600 delay-100"
        >
          Effortless ethnic, everyday grace.
        </h1>

        <p
          data-animate
          className="text-white/85 text-sm md:text-base leading-relaxed max-w-lg mb-10 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600 delay-200"
        >
          Discover the Flower Printed Set — a cotton kurta + pyjama combo
          designed for comfort and style. Made for women who choose
          elegance every day.
        </p>

        <div
          data-animate
          className="flex flex-col items-center gap-4 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600 delay-300"
        >
          <Link
            to="/shop"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-lg text-base font-semibold hover:bg-secondary-500 transition-colors whitespace-nowrap shadow-sm"
          >
            Shop Now
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
          <button
            type="button"
            onClick={() => openModal("Flower Printed Set")}
            className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-secondary-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line w-3.5 h-3.5 flex items-center justify-center" />
            Or order via WhatsApp
          </button>
        </div>
      </div>
    </section>
  );
}